import { employerActions } from 'redux/employer/employerSlice'
import Alert from 'core-system/Icons/Misc/Alert'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StaticAssetsUrl } from 'redux/config/services/Constants'
import styled from 'styled-components'
import ManageHrisModal from './ManageHrisModal'
import { AppState } from 'redux/config/store'
import { useLocation, useSearchParams } from 'react-router-dom'
import UrlParser from 'shared/UrlParser'
import RandomUtils from 'shared/RandomUtils'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import FinchConnect from 'features/Finch/components/FinchConnect'
import HrisReconnectModal from './HrisReconnectModal'

const ProviderImg = styled.img`
  height: 1.5rem;
  width: 1.5rem;
  border-radius: 50%;
`

interface ConnectHrisProps {
  providerName: string
  reAuth: boolean
  active?: boolean
  width?: 'full' | 'auto'
}

const ConnectHris = (props: ConnectHrisProps) => {
  const location = useLocation()
  const qp = UrlParser.getQueryParams(location.search)
  const [searchParams, setSearchParams] = useSearchParams(qp)
  const { providerName, reAuth, active, width = 'auto' } = props
  const [manageHrisModal, setManageHrisModal] = React.useState(false)
  const [showSelectProvidersModal, setShowSelectProvidersModal] =
    React.useState(false)
  const [providerNameChanged, setProviderNameChanged] = React.useState(false)
  // Will this be an issue in the future if the token exchange fails on Finch's end?
  const onSuccess = ({ code }) => {
    if (code !== null) {
      dispatch(employerActions.integrateHris(code))
    }
    setProviderNameChanged(true)
  }

  const dispatch = useDispatch()
  const { payrollProviderId, provider } = useSelector(
    (state: AppState) => state.employer.profile
  )

  // After initial Finch integration, open manage modal
  React.useEffect(() => {
    if (payrollProviderId && providerNameChanged && !reAuth) {
      setManageHrisModal((prevManageHrisModal) => !prevManageHrisModal)
    }
  }, [payrollProviderId, providerNameChanged, reAuth])

  const toggleManageModal = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement | any>) => {
      e.stopPropagation()
      setManageHrisModal(!manageHrisModal)
      // segment analytics
    },
    [manageHrisModal]
  )

  const disconnect = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      toggleManageModal(e)
      dispatch(employerActions.disconnectHris())
    },
    [dispatch, toggleManageModal]
  )

  const handleSelectProvidersModal = React.useCallback(() => {
    setShowSelectProvidersModal(true)
  }, [])

  useEffect(() => {
    if (searchParams.get('show_finch_connect') === 'true') {
      setShowSelectProvidersModal(true)
      setSearchParams({})
    }
    // eslint-disable-next-line
  }, [])

  // No HRIS connected
  if (!providerName) {
    return (
      <div>
        <PrimaryButton
          onClick={handleSelectProvidersModal}
          fullWidth={width === 'full'}
        >
          Connect to HRIS Integration
        </PrimaryButton>
        <FinchConnect
          open={showSelectProvidersModal}
          onClose={() => setShowSelectProvidersModal(false)}
          onSuccess={onSuccess}
        />
      </div>
    )
  }

  // HRIS connected but needs reauth
  if (providerName && reAuth) {
    return (
      <>
        <PrimaryButton
          startIcon={<Alert />}
          onClick={toggleManageModal}
          fullWidth={width === 'full'}
        >
          Reconnect
        </PrimaryButton>
        <HrisReconnectModal
          providerName={providerName}
          openModal={manageHrisModal}
          closeModal={toggleManageModal}
          onSuccess={onSuccess}
        />
      </>
    )
  }

  // Connected and Authenticated
  return (
    <>
      <PrimaryButton
        style={{ textTransform: 'capitalize' }}
        disabled={active}
        startIcon={
          <ProviderImg
            src={`${StaticAssetsUrl}/hris_provider_logos/${providerName}.png`}
            alt={providerName}
          />
        }
        onClick={toggleManageModal}
        fullWidth={width === 'full'}
      >
        Manage {RandomUtils.formatFinchProviderName(providerName)}
      </PrimaryButton>
      <ManageHrisModal
        providerName={providerName}
        reAuth={reAuth}
        closeModal={toggleManageModal}
        open={manageHrisModal}
        openFinchConnect={open}
        disconnectHris={disconnect}
      />
    </>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ConnectHris.displayName = 'ConnectHris'
}

export default ConnectHris
